import { FC } from 'react';
import { useFieldArray } from 'react-hook-form';
import { Flex, Grid, GridItem, Stack } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import IconButton from 'components/_core/IconButton';

import { emptyRow, useFormContext } from '../../form';
import VatRateInput from './components/VatRateInput';
import { computeVatAmountFromRate } from './helpers';
import locales from './locales';

interface VatAmountFormProps {
  transactionAmount: number;
  alignItems?: 'flex-start' | 'center';
}

const VatAmountForm: FC<VatAmountFormProps> = ({
  alignItems = 'flex-start',
  transactionAmount,
}) => {
  const { control, formState, register, setValue } = useFormContext();
  const { append, fields, remove } = useFieldArray({ control, name: 'rows' });

  return (
    <Stack alignItems={alignItems} spacing="space-12">
      {fields.map((field, index) => (
        <Grid
          columnGap="space-16"
          key={field.id}
          rowGap="space-12"
          templateColumns="repeat(10, 1fr)"
        >
          <GridItem colSpan={3}>
            <VatRateInput
              computeVatAmountFromRate={(rate: number) =>
                computeVatAmountFromRate(rate, transactionAmount)
              }
              index={index}
              label={index === 0 ? locales.rate : ''}
            />
          </GridItem>

          <GridItem colSpan={7}>
            <Form.Field
              error={formState.errors?.rows?.[index]?.trueVatAmount?.message}
              label={index === 0 ? locales.amount : ''}
            >
              <Flex alignItems="center" gap="space-12">
                <Form.Input
                  required
                  type="number"
                  {...register(`rows.${index}.trueVatAmount`)}
                />

                <IconButton
                  aria-label=""
                  icon="trash"
                  onClick={() => {
                    if (fields.length === 1) {
                      setValue('rows', [emptyRow], { shouldDirty: true });
                      return;
                    }

                    remove(index);
                  }}
                  variant="secondary"
                />
              </Flex>
            </Form.Field>
          </GridItem>
        </Grid>
      ))}

      <Button
        icon="plus"
        iconPosition="left"
        onClick={() => append(emptyRow)}
        variant="inline-secondary"
      >
        {locales.addRow}
      </Button>
    </Stack>
  );
};

export default VatAmountForm;
